/**
 * EPIC-DM01-F: Activation Form Component (Redesigned)
 * Password setup form with strength indicator, using demo design system
 */

import { useState, useMemo } from 'react';
import { AlertCircle, CheckCircle2, Eye, EyeOff, Loader2, Lock } from 'lucide-react';
import { DemoButton, DemoCard } from '@/components/demo/ui';
import { cn } from '@/lib/utils';

interface ActivationFormProps {
  maskedEmail: string;
  prospectName?: string;
  onSubmit: (password: string) => Promise<void>;
  isSubmitting: boolean;
  error?: Error | null;
}

interface PasswordStrength {
  score: number;
  label: string;
  colorClass: string;
  checks: {
    length: boolean;
    hasLetter: boolean;
    hasNumber: boolean;
    hasSpecial: boolean;
  };
}

function calculatePasswordStrength(password: string): PasswordStrength {
  const checks = {
    length: password.length >= 8,
    hasLetter: /[a-zA-Z]/.test(password),
    hasNumber: /[0-9]/.test(password),
    hasSpecial: /[^a-zA-Z0-9]/.test(password),
  };

  const passedChecks = Object.values(checks).filter(Boolean).length;

  let score = 0;
  let label = 'Too weak';
  let colorClass = 'bg-demo-error';

  if (password.length === 0) {
    return { score: 0, label: '', colorClass: 'bg-gray-200', checks };
  }

  if (passedChecks >= 2 && password.length >= 8) {
    score = 1;
    label = 'Weak';
    colorClass = 'bg-orange-500';
  }
  if (passedChecks >= 3 && password.length >= 8) {
    score = 2;
    label = 'Fair';
    colorClass = 'bg-demo-gold';
  }
  if (passedChecks >= 3 && password.length >= 10) {
    score = 3;
    label = 'Good';
    colorClass = 'bg-lime-500';
  }
  if (passedChecks >= 4 && password.length >= 12) {
    score = 4;
    label = 'Strong';
    colorClass = 'bg-demo-green';
  }

  return { score, label, colorClass, checks };
}

export function ActivationForm({
  maskedEmail,
  prospectName,
  onSubmit,
  isSubmitting,
  error,
}: ActivationFormProps) {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [touched, setTouched] = useState({ password: false, confirm: false });

  const strength = useMemo(() => calculatePasswordStrength(password), [password]);

  const isValid =
    strength.checks.length &&
    strength.checks.hasLetter &&
    strength.checks.hasNumber &&
    password === confirmPassword &&
    password.length > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid) {
      setTouched({ password: true, confirm: true });
      return;
    }
    await onSubmit(password);
  };

  return (
    <form onSubmit={handleSubmit}>
      <DemoCard>
        <div className="space-y-5">
          {/* Header */}
          <div className="text-center">
            <Lock className="h-8 w-8 mx-auto mb-2 text-demo-green" />
            <h2 className="text-xl font-bold text-demo-text">Set Your Password</h2>
            <p className="text-sm text-demo-text-muted mt-1">
              {prospectName ? `Welcome, ${prospectName}!` : 'Welcome!'} Create a password to start your trial.
            </p>
          </div>

          {/* Account email badge */}
          <div className="bg-demo-surface-alt rounded-lg p-3 text-center">
            <p className="text-xs text-demo-text-muted">Account email</p>
            <p className="font-medium text-demo-text text-sm">{maskedEmail}</p>
          </div>

          {/* Error */}
          {error && (
            <div className="bg-demo-error-light rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="h-5 w-5 mt-0.5 shrink-0 text-demo-error" />
              <div>
                <p className="font-medium text-demo-error text-sm">Error</p>
                <p className="text-xs text-demo-error/80">{error.message}</p>
              </div>
            </div>
          )}

          {/* Password field */}
          <div className="space-y-2">
            <label htmlFor="password" className="block text-sm font-medium text-demo-text">
              Password
            </label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onBlur={() => setTouched((t) => ({ ...t, password: true }))}
                placeholder="Enter your password"
                autoComplete="new-password"
                className="w-full h-[var(--demo-touch-min)] px-4 pr-12 rounded-xl border border-gray-200 bg-white text-demo-text text-[length:var(--demo-font-base)] focus:outline-none focus:ring-2 focus:ring-demo-green focus:border-transparent"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-demo-text-muted"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>

            {/* Strength bar */}
            {password.length > 0 && (
              <div className="space-y-1.5">
                <div className="flex gap-1">
                  {[0, 1, 2, 3].map((i) => (
                    <div
                      key={i}
                      className={cn(
                        'h-1.5 flex-1 rounded-full transition-colors',
                        i < strength.score ? strength.colorClass : 'bg-gray-200'
                      )}
                    />
                  ))}
                </div>
                <p className={cn(
                  'text-xs',
                  strength.score < 2 ? 'text-demo-error' : 'text-demo-text-muted'
                )}>
                  {strength.label}
                </p>
              </div>
            )}

            {/* Requirements checklist */}
            {touched.password && password.length > 0 && (
              <div className="space-y-1 pt-1">
                <RequirementCheck met={strength.checks.length} text="At least 8 characters" />
                <RequirementCheck met={strength.checks.hasLetter} text="Contains a letter" />
                <RequirementCheck met={strength.checks.hasNumber} text="Contains a number" />
              </div>
            )}
          </div>

          {/* Confirm password field */}
          <div className="space-y-2">
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-demo-text">
              Confirm Password
            </label>
            <div className="relative">
              <input
                id="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                onBlur={() => setTouched((t) => ({ ...t, confirm: true }))}
                placeholder="Confirm your password"
                autoComplete="new-password"
                className={cn(
                  'w-full h-[var(--demo-touch-min)] px-4 pr-12 rounded-xl border bg-white text-demo-text text-[length:var(--demo-font-base)] focus:outline-none focus:ring-2 focus:ring-demo-green focus:border-transparent',
                  touched.confirm && confirmPassword.length > 0 && password !== confirmPassword
                    ? 'border-demo-error'
                    : 'border-gray-200'
                )}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-demo-text-muted"
                tabIndex={-1}
              >
                {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
            {touched.confirm && confirmPassword.length > 0 && password !== confirmPassword && (
              <p className="text-xs text-demo-error">Passwords do not match</p>
            )}
            {touched.confirm && confirmPassword.length > 0 && password === confirmPassword && (
              <p className="text-xs text-demo-green-dark flex items-center gap-1">
                <CheckCircle2 className="h-3.5 w-3.5" />
                Passwords match
              </p>
            )}
          </div>

          {/* Submit */}
          <DemoButton
            type="submit"
            variant="primary"
            size="lg"
            fullWidth
            loading={isSubmitting}
            disabled={isSubmitting || !isValid}
          >
            Activate Account
          </DemoButton>
        </div>
      </DemoCard>
    </form>
  );
}

function RequirementCheck({ met, text }: { met: boolean; text: string }) {
  return (
    <div className={cn(
      'flex items-center gap-2 text-xs',
      met ? 'text-demo-green-dark' : 'text-demo-text-muted'
    )}>
      {met ? (
        <CheckCircle2 className="h-3.5 w-3.5" />
      ) : (
        <div className="h-3.5 w-3.5 rounded-full border border-gray-300" />
      )}
      {text}
    </div>
  );
}
